import { AfterViewInit, Component, EventEmitter, Input, NgModule, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { AppBaseModule } from '../../modules/app-base.module';
import { SaraPlusFormModule } from '@sp-forms/saraplus-ngx-forms';
import { PrimeNGModule } from '../../modules/primeng.module';
import { Idle, NgIdleModule, DocumentInterruptSource, EventTargetInterruptOptions } from '@ng-idle/core';
import { Subscription } from 'rxjs';
@Component({
    selector: 'app-session-timeout',
    templateUrl: './session-timeout.component.html',
    styleUrl: './session-timeout.component.scss',
    standalone: false
})
export class SessionTimeoutComponent implements OnInit, AfterViewInit, OnDestroy {


  #userIdleService = inject(Idle);
  lastPing: Date = new Date();
  timer: number = 60;
  visible: boolean = false;
  #actionTaken = false;

  @Input() setting: SessionSetting = { idle: 1500, timeout: 300, ping: 60 }
  @Output() ContinueClicked?: EventEmitter<void> = new EventEmitter();
  @Output() SessionExpired?: EventEmitter<void> = new EventEmitter();
  @Output() LogoutClicked?: EventEmitter<void> = new EventEmitter();
  subscriptions = new Subscription()

  ngOnInit(): void {
    this.#userIdleService.setIdle(this.setting.idle);
    this.#userIdleService.setTimeout(this.setting.timeout);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.#userIdleService.setInterrupts(this.createCustomInterruptSources(null));
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(this.#userIdleService.onIdleEnd.subscribe(() => {
      this.reset();
      if (this.ContinueClicked) {
        this.ContinueClicked.emit();
      }
      this.closeDialog();
    }));

    this.subscriptions.add(this.#userIdleService.onTimeout.subscribe(() => {
      if (this.SessionExpired) {
        this.SessionExpired.emit();
      }
      this.closeDialog();
    }));

    this.subscriptions.add(this.#userIdleService.onIdleStart.subscribe(() => {
      this.timer = this.setting.timeout;
      this.visible = true;
    }));

    this.subscriptions.add(this.#userIdleService.onTimeoutWarning.subscribe((countdown) => {
      this.timer = countdown;
    }));

    this.subscriptions.add(this.#userIdleService.onInterrupt.subscribe(() => {
      const secDifference = this.getDateDifferenceInSeconds(this.lastPing, new Date());
      if ((this.setting.ping ?? 60) <= secDifference) {
        this.ContinueClicked.emit();
        this.lastPing = new Date();
      }
    }));

    this.startWatching();
  }

  get displayTimer(): string {
    const minutes = Math.floor(this.timer / 60);
    const seconds = this.timer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get displayMinutes(): string {
    const minutes = Math.max(1, Math.ceil(this.timer / 60));
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  reset() {
    this.#userIdleService.watch();
  }

  onContinueClicked() {
    this.reset();
    this.closeDialog();
    this.ContinueClicked?.emit();
  }

  onLogoutClicked() {
    this.stopWatching();
    this.closeDialog();
    this.LogoutClicked?.emit();
  }

  onDialogHide() {
    if (!this.#actionTaken) {
      this.reset();
      this.ContinueClicked?.emit();
    }
    this.#actionTaken = false;
  }

  closeDialog() {
    this.#actionTaken = true;
    this.visible = false;
  }

  stopWatching() {
    this.#userIdleService.stop();
  }

  startWatching() {
    this.#userIdleService.watch()
  }

  createCustomInterruptSources(options: EventTargetInterruptOptions) {
    return [
      new DocumentInterruptSource('keydown mouseup click touchstart touchmove scroll', options)
    ];
  }

  getDateDifferenceInSeconds(date1: Date, date2: Date): number {
    const diffInMs = date2.getTime() - date1.getTime();
    return diffInMs / (1000);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Idle is a root singleton (NgIdleModule.forRoot); only stop it so it
    // re-arms cleanly on the next login. Calling its ngOnDestroy() tears down the
    // shared instance and breaks idle detection after the first logout.
    this.stopWatching();
  }
}

export interface SessionSetting {
  idle: number,
  timeout: number,
  ping?: number
}

@NgModule({
  declarations: [
    SessionTimeoutComponent
  ],
  imports: [
    AppBaseModule,
    SaraPlusFormModule,
    PrimeNGModule,
    NgIdleModule.forRoot()
  ],
  exports: [
    SessionTimeoutComponent
  ]
})
export class SessionTimeoutModule {

}
